const MAX_SIDE = 1600;
const JPEG_QUALITY = 0.82;

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read the photo"));
    };
    img.src = url;
  });
}

const encode = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));

/**
 * Shrinks a phone photo before upload (a 12 MP HEIC/JPEG becomes a few hundred
 * KB). `type` "image/webp" gives ~40% smaller files than JPEG at the same look;
 * browsers that can't encode WebP (older Safari) silently hand back PNG, so we
 * check the result and fall back to JPEG. Falls back to the original file if
 * the browser can't decode it into a canvas at all.
 */
export async function prepareImage(
  file: Blob,
  maxSide = MAX_SIDE,
  quality = JPEG_QUALITY,
  type: "image/jpeg" | "image/webp" = "image/jpeg",
): Promise<Blob> {
  try {
    const img = await loadImage(file);
    const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    let blob = await encode(canvas, type, quality);
    if (type !== "image/jpeg" && blob?.type !== type) blob = await encode(canvas, "image/jpeg", quality);
    return blob ?? file;
  } catch {
    return file;
  }
}
